/**
 * Formats a number with K/M/B suffixes for compact display.
 * Values under 1000 are returned as-is.
 *
 * @example
 * compactNumber(999)            // "999"
 * compactNumber(1500)           // "1.5K"
 * compactNumber(1_200_000)      // "1.2M"
 * compactNumber(2_500_000_000)  // "2.5B"
 * compactNumber(1500, 0)        // "2K"
 */
export const compactNumber = (n: number, fractionDigits = 1): string =>
  new Intl.NumberFormat("en", {
    notation: "compact",
    maximumFractionDigits: fractionDigits,
  }).format(n);

/**
 * Formats a USD amount with just enough decimal places to show its first
 * significant digit. Two decimals covers anything a cent or more; smaller
 * amounts -- a fraction of a cent per request or per token -- need more, or
 * they round to "$0.00" and read as free.
 *
 * @example
 * formatPreciseCurrency(5.1667)       // "$5.17"
 * formatPreciseCurrency(0.035)        // "$0.035"
 * formatPreciseCurrency(0.00000196)   // "$0.0000020"
 * formatPreciseCurrency(0)            // "$0.00"
 */
export const formatPreciseCurrency = (value: number): string =>
  value.toLocaleString("en", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits:
      value === 0
        ? 2
        : Math.max(2, 1 - Math.floor(Math.log10(Math.abs(value)))),
  });
